import { Fragment, ReactNode, useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import ShareDocumentButton from '~/components/Workflow/shared/ShareDocumentButton';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TicketNegotiationStatus } from '~/enums/TicketNegotiationStatus';
import { TicketPermissionType } from '~/enums/TicketPermissionType';
import { TicketStageType } from '~/enums/TicketStageType';
import { SendToEsignature } from '~/features/ticket/actions/SendToEsignature';
import { SignDocument } from '~/features/ticket/actions/SignDocument';
import { ViewOnEsignPlatform } from '~/features/ticket/actions/ViewOnEsignPlatform';
import { EsignatureEnvelopeStatusValue } from '~/features/ticket/types/esignature';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useUsers } from '~/hooks/useUsers';
import { getIsNextStageDisabled } from '~/reducers/ticket';
import { api } from '~/redux/api/createApi';
import { useAppSelector } from '~/rootState';
import { TicketStateView } from '~/types/Ticket';
import {
  getNextStageType,
  getSimplifiedStageNames,
  latestVersionHasWorkingDraft,
  shouldFillFormQuestionsInSignStage,
  shouldFillFormQuestionsInSignStageEsignatureRevamp,
} from '~/utils/ticket';

import UploadSignedButton from '../UploadSignedButton/UploadSignedButton';
import BackToReviewAction from './BackToReviewAction';
import CheckSignatureStatus from './CheckSignatureStatus';
import CollectSignatureAction from './CollectSignaturesAction';
import MarkCompleteAction from './MarkCompleteAction';
import { RetractFromSupplierPortalModal } from './RetractFromSupplierPortalModal';
import { SendToSupplierPortalModal } from './SendToSupplierPortalModal';
import { TicketNegotiationStatusDisplay } from './TicketNegotiationStatusDisplay';
import UploadVersionAction from './UploadVersionAction';
import { useTicketSupplierNegotiation } from './useTicketSupplierNegotiation';

interface TicketDocumentActionsHandlers {
  handleOnDownloadClick: () => void;
  handleOnShareSuccess: () => void;
  handleNextStageClick: () => void;
  handleOnAddSignersClick: () => void;
  handleSendForSigning: () => void;
}

interface TicketDocumentActionsLoaders {
  isLoadingDownload: boolean;
  isPreparingDownload: boolean;
  isLoadingNextStage: boolean;
  isLoadingSendForSigning: boolean;
}

interface TicketDocumentActionsProps {
  handlers: TicketDocumentActionsHandlers;
  loaders: TicketDocumentActionsLoaders;
}

const rankedPermissions = [
  TicketPermissionType.BackToReview,
  TicketPermissionType.UploadVersion,
  TicketPermissionType.Download,
  TicketPermissionType.Share,
  TicketPermissionType.SendForReview,
  TicketPermissionType.SendForSignatures,
  TicketPermissionType.UploadSigned,
  TicketPermissionType.CollectSignatures,
  TicketPermissionType.EditForm,
  TicketPermissionType.MarkForSignatures,
  TicketPermissionType.MarkComplete,
  TicketPermissionType.SendForFinalization,
];

export const getNextStageButtonText = (
  stage: TicketStageType,
  stages: TicketStageType[],
  isEsignatureV2RevampEnabled: boolean,
  intl: IntlShape,
): string | null => {
  switch (getNextStageType(stage, stages)) {
    case TicketStageType.Review:
      return intl.formatMessage({
        id: 'TicketDocumentActions.sendForReview',
        defaultMessage: 'Move to Review Stage',
        description: 'Button text for sending document to review stage',
      });
    case TicketStageType.Sign:
      return isEsignatureV2RevampEnabled
        ? intl.formatMessage({
            id: 'TicketDocumentActions.sendToSignStage',
            defaultMessage: 'Move to Sign Stage',
            description: 'Button text for sending document to sign stage',
          })
        : intl.formatMessage({
            id: 'TicketDocumentActions.sendForSigning',
            defaultMessage: 'Move to Sign Stage',
            description: 'Button text for sending document to sign stage',
          });
    case TicketStageType.Finalize:
      return intl.formatMessage({
        id: 'TicketDocumentActions.sendForFinalization',
        defaultMessage: 'Move to Finalize Stage',
        description: 'Button text for sending document to finalize stage',
      });
    default:
      return null;
  }
};

const lastLeftSideActionIndex = rankedPermissions.indexOf(
  TicketPermissionType.Share,
);

function uploadVersionButton(stage: TicketStageType, disabled: boolean) {
  if ([TicketStageType.Edit, TicketStageType.Review].includes(stage)) {
    return <UploadVersionAction disabled={disabled} />;
  }
}

function TicketDocumentActions({
  handlers,
  loaders,
}: TicketDocumentActionsProps) {
  const ticket = useAppSelector(
    (state) => state.ticket,
  ) as unknown as TicketStateView;
  const users = useUsers();
  const [
    isSendToSupplierPortalModalVisible,
    setIsSendToSupplierPortalModalVisible,
  ] = useState(false);
  const [isRetractConfirmModalVisible, setIsRetractConfirmModalVisible] =
    useState(false);

  const {
    id,
    permissions,
    stage,
    enabledStageNames,
    documentVersions,
    document,
    contractRecordUniqueId,
    negotiationStatus,
    wssDesignated,
  } = ticket;
  const isNegotiationInProgress =
    negotiationStatus === TicketNegotiationStatus.InProgress;

  const { postRetract, isLoading: isRetracting } =
    useTicketSupplierNegotiation();
  const currentVersion = documentVersions?.find(
    (v) => v.versionNumber === document?.version,
  );
  const versionId = currentVersion?.id ?? null;
  const contractId = contractRecordUniqueId ?? null;
  const isSendToSupplierPortalStage = [
    TicketStageType.Edit,
    TicketStageType.Review,
  ].includes(stage);
  const canOpenSendToSupplierPortal =
    isSendToSupplierPortalStage && Boolean(versionId) && Boolean(contractId);

  const nonDuplicatePermissions = permissions.filter(
    (permission, index) => permissions.indexOf(permission) === index,
  );
  const isHideShareButtonEnabled = useFlag(FlagType.HideShareButton);
  const isEsignatureV2RevampEnabled = useFlag(FlagType.EsignatureV2Revamp);
  const isDocuSignSignWithEmailEnabled = useFlag(
    FlagType.DocuSignSignWithEmail,
  );
  const intl = useIntl();

  const isNextStageDisabled =
    isEsignatureV2RevampEnabled && stage === TicketStageType.Sign
      ? false
      : getIsNextStageDisabled(ticket);

  const stages = getSimplifiedStageNames(ticket, intl);

  const isReviewStageEnabled = enabledStageNames.includes(
    TicketStageType.Review,
  );
  const { data: envelope } = api.endpoints.getEnvelope.useQuery(
    {
      ticketId: id,
    },
    {
      skip: !isEsignatureV2RevampEnabled || stage !== TicketStageType.Sign,
    },
  );
  const { data: esignatureRecipients } = api.endpoints.getRecipients.useQuery(
    {
      ticketId: id,
    },
    {
      skip: !isEsignatureV2RevampEnabled || stage !== TicketStageType.Sign,
    },
  );
  const esignatureSigners = esignatureRecipients?.signers;
  const isInEsignRevampedSignStage =
    isEsignatureV2RevampEnabled && stage === TicketStageType.Sign;
  const isRevampSigningCompleted =
    envelope?.status?.value === EsignatureEnvelopeStatusValue.Signed ||
    esignatureSigners?.length === 0;
  const isRevampedSignStageInProgress =
    isInEsignRevampedSignStage && !isRevampSigningCompleted;
  const isWSSIntegrationM4SupplierNegotiationEnabled = useFlag(
    FlagType.WSSIntegrationM4SupplierNegotiation,
  );
  const shouldRenderWssActions =
    isWSSIntegrationM4SupplierNegotiationEnabled && wssDesignated;

  const isMissingIntakeFormSignerData = isEsignatureV2RevampEnabled
    ? shouldFillFormQuestionsInSignStageEsignatureRevamp(
        ticket,
        envelope?.status?.value,
      )
    : shouldFillFormQuestionsInSignStage(ticket, users);

  const handleRetractConfirm = async () => {
    if (!versionId || !contractId) return;
    try {
      await postRetract({ ticketId: id, versionId, contractId });
      setIsRetractConfirmModalVisible(false);
    } catch {
      // Error is surfaced by RTK Query / global error handling
    }
  };

  const getShareButtonTooltip = () => {
    if (latestVersionHasWorkingDraft(ticket.documentVersions)) {
      return intl.formatMessage({
        id: 'TicketDocumentActions.shareButtonTooltipWithDraft',
        defaultMessage:
          'There are edits to this version in progress. Please save as a new version, or delete your edits to share this version.',
        description:
          'Tooltip shown when share button is disabled due to working draft',
      });
    }
    return intl.formatMessage({
      id: 'TicketDocumentActions.shareButtonTooltip',
      defaultMessage: 'Share this document version.',
      description: 'Tooltip for share button',
    });
  };

  const nextStageButton = () => {
    const nextStageType = getNextStageType(stage, stages);
    const nextStageText = getNextStageButtonText(
      stage,
      stages,
      isEsignatureV2RevampEnabled,
      intl,
    );
    const onClick =
      nextStageType === TicketStageType.Sign
        ? handlers.handleSendForSigning
        : handlers.handleNextStageClick;

    const isLoading =
      nextStageType === TicketStageType.Sign
        ? loaders.isLoadingSendForSigning
        : loaders.isLoadingNextStage;

    if (!nextStageText) return null;

    const isDisabled =
      isNextStageDisabled ||
      isRevampedSignStageInProgress ||
      isNegotiationInProgress;

    return (
      <Button
        isLoading={isLoading}
        text={nextStageText}
        onClick={onClick}
        variant="primary"
        disabled={isDisabled}
      />
    );
  };

  const documentActions: Record<string, ReactNode> = {
    [TicketPermissionType.BackToReview]: isReviewStageEnabled && (
      <BackToReviewAction disabled={isNegotiationInProgress} />
    ),
    [TicketPermissionType.UploadVersion]: uploadVersionButton(
      ticket.stage,
      isNegotiationInProgress,
    ),
    [TicketPermissionType.Download]: (
      <Button
        tooltip={
          loaders.isPreparingDownload
            ? intl.formatMessage({
                id: 'TicketDocumentActions.downloadPreparingTooltip',
                defaultMessage:
                  'Your document is getting ready to be downloaded.',
                description: 'Tooltip shown when download is being prepared',
              })
            : ''
        }
        isLoading={loaders.isPreparingDownload || loaders.isLoadingDownload}
        text={intl.formatMessage({
          id: 'TicketDocumentActions.downloadButton',
          defaultMessage: 'Download',
          description: 'Download button text',
        })}
        onClick={handlers.handleOnDownloadClick}
        variant="secondary"
      />
    ),
    [TicketPermissionType.Share]: (
      <Layout align="center" spacing={4}>
        {shouldRenderWssActions &&
          (isNegotiationInProgress ? (
            <Button
              text={intl.formatMessage({
                id: 'TicketDocumentActions.retractFromSupplierPortalButton',
                defaultMessage: 'Retract from Supplier Portal',
                description:
                  'Button text for retracting document from supplier portal; Clicking on it opens a modal to confirm the action',
              })}
              onClick={() => setIsRetractConfirmModalVisible(true)}
              isLoading={isRetracting}
              variant="secondary"
            />
          ) : (
            <Button
              disabled={!canOpenSendToSupplierPortal || isNegotiationInProgress}
              text={intl.formatMessage({
                id: 'TicketDocumentActions.sendToSupplierPortalButton',
                defaultMessage: 'Send to Supplier Portal',
                description:
                  'Button text for sending document to supplier portal',
              })}
              onClick={() => setIsSendToSupplierPortalModalVisible(true)}
              variant="secondary"
            />
          ))}

        {!isHideShareButtonEnabled && (
          <ShareDocumentButton
            isDisabled={
              latestVersionHasWorkingDraft(ticket.documentVersions) ||
              isNegotiationInProgress
            }
            tooltip={isNegotiationInProgress ? '' : getShareButtonTooltip()}
            onShare={handlers.handleOnShareSuccess}
          />
        )}
      </Layout>
    ),
    [TicketPermissionType.SendForReview]: nextStageButton(),
    [TicketPermissionType.SendForSignatures]: nextStageButton(),
    [TicketPermissionType.SendForFinalization]: nextStageButton(),
    [TicketPermissionType.EditForm]: isMissingIntakeFormSignerData && (
      <Button
        text={intl.formatMessage({
          id: 'TicketDocumentActions.addSignersButton',
          defaultMessage: 'Add Signers',
          description: 'Button text for adding signers',
        })}
        onClick={handlers.handleOnAddSignersClick}
        tooltip={intl.formatMessage({
          id: 'TicketDocumentActions.addSignersTooltip',
          defaultMessage: 'Add missing signers to collect signatures',
          description: 'Tooltip for add signers button',
        })}
      />
    ),
    [TicketPermissionType.UploadSigned]: <UploadSignedButton />,
    [TicketPermissionType.CollectSignatures]: isEsignatureV2RevampEnabled ? (
      <SendToEsignature />
    ) : (
      <CollectSignatureAction />
    ),
    [TicketPermissionType.MarkForSignatures]: (
      <Button
        text={intl.formatMessage({
          id: 'TicketDocumentActions.markSignaturesButton',
          defaultMessage: 'Mark Signatures',
          description: 'Button text for marking signatures',
        })}
        onClick={() => console.log('Mark Signatures clicked;')}
      />
    ),
    [TicketPermissionType.MarkComplete]:
      getNextStageType(stage, stages) === null &&
      !isNextStageDisabled &&
      !isRevampedSignStageInProgress ? (
        <MarkCompleteAction disabled={isNegotiationInProgress} />
      ) : null,
  };

  const leftSidePermissions: TicketPermissionType[] = [];
  const rightSidePermissions: TicketPermissionType[] = [];

  nonDuplicatePermissions.forEach((permission) => {
    const actionPermission = documentActions[permission];
    if (actionPermission) {
      if (rankedPermissions.indexOf(permission) > lastLeftSideActionIndex) {
        rightSidePermissions.push(permission);
      } else {
        leftSidePermissions.push(permission);
      }
    }
  });

  leftSidePermissions.sort(
    (p1, p2) => rankedPermissions.indexOf(p1) - rankedPermissions.indexOf(p2),
  );
  rightSidePermissions.sort(
    (p1, p2) => rankedPermissions.indexOf(p1) - rankedPermissions.indexOf(p2),
  );

  return (
    <>
      <Layout direction="column" spacing={3}>
        {ticket.negotiationStatus && (
          <TicketNegotiationStatusDisplay status={ticket.negotiationStatus} />
        )}

        <Layout wrap align="center" justify="space-between" spacing={2}>
          <Layout align="center" spacing={4}>
            {leftSidePermissions.map((permission) => (
              <Fragment key={`left-side-action-${permission}`}>
                {documentActions[permission]}
              </Fragment>
            ))}
            {isEsignatureV2RevampEnabled &&
              stage === TicketStageType.Sign &&
              envelope &&
              envelope.status.value !==
                EsignatureEnvelopeStatusValue.Signed && <ViewOnEsignPlatform />}
          </Layout>
          <Layout align="center" spacing={4}>
            {rightSidePermissions.map((permission) => (
              <Fragment key={`right-side-action-${permission}`}>
                {documentActions[permission]}
              </Fragment>
            ))}
            {ticket.hasEnvelope &&
              stage === TicketStageType.Sign &&
              !isEsignatureV2RevampEnabled && <CheckSignatureStatus />}
            {isEsignatureV2RevampEnabled && !isDocuSignSignWithEmailEnabled && (
              <SignDocument isSignStage={stage === TicketStageType.Sign} />
            )}
          </Layout>
        </Layout>
      </Layout>
      {isSendToSupplierPortalModalVisible && (
        <SendToSupplierPortalModal
          onCancel={() => setIsSendToSupplierPortalModalVisible(false)}
          onSuccess={() => setIsSendToSupplierPortalModalVisible(false)}
        />
      )}
      {isRetractConfirmModalVisible && (
        <RetractFromSupplierPortalModal
          onCancel={() => setIsRetractConfirmModalVisible(false)}
          onConfirm={handleRetractConfirm}
          isLoading={isRetracting}
        />
      )}
    </>
  );
}

export default TicketDocumentActions;
